import { isTenantPlatformOrigin } from "./tenant-host";

/**
 * QL-776 — the `iframe` flow component shows a designer-chosen third-party page
 * by URL. The URL is the whole security surface, so one pure rule is shared by
 * the API service (enforces), the dialogs (UX), the renderer and the one-time
 * embed conversion.
 */
export const IFRAME_URL_MAX_LENGTH = 2048;
export const IFRAME_HEIGHT_MIN = 100;
export const IFRAME_HEIGHT_MAX = 4000;
export const IFRAME_HEIGHT_DEFAULT = 500;
export const IFRAME_ASPECT_RATIOS = ["16:9", "4:3", "1:1"] as const;
export type IframeAspectRatio = (typeof IFRAME_ASPECT_RATIOS)[number];
export type IframeUrlProblem =
  | "missing"
  | "not_string"
  | "too_long"
  | "invalid"
  | "not_https"
  | "credentials";

/** null = acceptable. Absolute https only; `new URL` must parse; no userinfo; bounded length. */
export function iframeUrlProblem(url: unknown): IframeUrlProblem | null {
  if (url === undefined || url === null || url === "") return "missing";
  if (typeof url !== "string") return "not_string";
  const trimmed = url.trim();
  if (trimmed.length === 0) return "missing";
  if (trimmed.length > IFRAME_URL_MAX_LENGTH) return "too_long";
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return "invalid";
  }
  // Rejects http:, javascript:, data:, blob: and relative URLs.
  if (parsed.protocol !== "https:") return "not_https";
  if (parsed.username || parsed.password) return "credentials";
  if (!parsed.hostname) return "invalid";
  return null;
}

export function isValidIframeHeight(h: unknown): h is number {
  return (
    typeof h === "number" && Number.isInteger(h) && h >= IFRAME_HEIGHT_MIN && h <= IFRAME_HEIGHT_MAX
  );
}

export function isIframeAspectRatio(r: unknown): r is IframeAspectRatio {
  return typeof r === "string" && (IFRAME_ASPECT_RATIOS as readonly string[]).includes(r);
}

/**
 * True when `url` points at the platform itself: WEB_URL's origin, the root
 * domain or a tenant host under it. The frame grants `allow-same-origin`, so
 * framing the app would hand the framed page the app's origin. The API enforces
 * this on save; the renderer also refuses its own current origin (preview hosts).
 */
export function isPlatformIframeUrl(
  url: string,
  webUrl: string,
  rootDomain: string | null | undefined,
): boolean {
  let origin: string;
  let web: URL;
  try {
    origin = new URL(url.trim()).origin;
    web = new URL(webUrl);
  } catch {
    return false;
  }
  return origin === web.origin || isTenantPlatformOrigin(origin, web.protocol, rootDomain);
}
